import React, { useState, useEffect, useMemo } from 'react';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import PdfDocument from './PdfDocument';
import AttachmentUploader from './AttachmentUploader';
import ExportFreeTextModal from '../shared/ExportFreeTextModal';
import { projectApi } from '../../../api/projectApi';
import { journalApi } from '../../../api/journalApi';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './SiteJournal.css';

export default function ExportModal({ isOpen, onClose, project, token, user }) {
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);
    const [allEntries, setAllEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // In-Modal Detail View
    const [selectedDetailEntry, setSelectedDetailEntry] = useState(null);

    // Project Date Limits
    const [projectMinDate, setProjectMinDate] = useState('');
    const [projectMaxDate, setProjectMaxDate] = useState('');

    // Title 
    const [reportTitle, setReportTitle] = useState('Bautagebuch');

    // Combinable Filters
    const [useTimeframe, setUseTimeframe] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [usePriorities, setUsePriorities] = useState(false);
    const [selectedPriorities, setSelectedPriorities] = useState({
        critical: true,
        high: true,
        medium: true,
        low: true
    });

    // Cover Page Options & Panel States
    const [coverOptions, setCoverOptions] = useState({
        showLocation: true,
        showStatus: true,
        showTeam: true,
        showBlacklist: true
    });
    const [teamMembers, setTeamMembers] = useState([]);

    // Panel Open States
    const [isCoverOpen, setIsCoverOpen] = useState(false);
    const [isTimeframeOpen, setIsTimeframeOpen] = useState(false);
    const [isPrioOpen, setIsPrioOpen] = useState(false);

    // Person Filter
    const [useAuthor, setUseAuthor] = useState(false);
    const [selectedAuthors, setSelectedAuthors] = useState({});
    const [availableAuthors, setAvailableAuthors] = useState([]);
    const [isAuthorOpen, setIsAuthorOpen] = useState(false);

    // Details Panel state
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Free Text State
    const [freeTextHtml, setFreeTextHtml] = useState('');
    const [freeTextPosition, setFreeTextPosition] = useState('between_team_entries');
    const [isFreeTextModalOpen, setIsFreeTextModalOpen] = useState(false);

    // Sorting
    const [sortOrder, setSortOrder] = useState('date_desc');

    const formatWeatherDisplay = (entry) => {
        let parts = [];
        if (entry.weather) parts.push(entry.weather);

        let min = '';
        let max = '';
        if (entry.weather_data && typeof entry.weather_data === 'object') {
            min = entry.weather_data.temp_min;
            max = entry.weather_data.temp_max;
        }

        if (min !== '' && min !== undefined && max !== '' && max !== undefined) {
            parts.push(`Max: ${max}°C, Min: ${min}°C`);
        }
        return parts.join(', ');
    };

    // Preview mode
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        if (isOpen && project) {
            // Reset state
            setShowPreview(false);
            setAllEntries([]);
            setReportTitle('Bautagebuch');
            setUseTimeframe(false);
            setUsePriorities(false);
            setSortOrder('date_desc');
            setSelectedPriorities({ critical: true, high: true, medium: true, low: true });
            setCoverOptions({ showLocation: true, showStatus: true, showTeam: true, showBlacklist: true });
            setTeamMembers([]);
            setIsCoverOpen(false);
            setIsTimeframeOpen(false);
            setIsPrioOpen(false);
            setSelectedAuthors({});
            setAvailableAuthors([]);
            setIsAuthorOpen(false);
            setIsDetailsOpen(false);
            setFreeTextHtml('');
            setFreeTextPosition('between_team_entries');
            setIsFreeTextModalOpen(false);

            fetchProjectDates();
            fetchTeamMembers();
        }
    }, [isOpen, project]);

    const fetchTeamMembers = async () => {
        try {
            const data = await projectApi.getProjectUsers(project.id);
            setTeamMembers(data);
        } catch (err) {
            console.error("Fehler beim Laden des Teams:", err);
        }
    };

    const fetchProjectDates = async () => {
        try {
            const data = await journalApi.getEntries(project.id);
            if (data && data.length > 0) {
                let dates = data.map(e => e.date).sort();
                const min = dates[0];
                const max = dates[dates.length - 1];
                setProjectMinDate(min);
                setProjectMaxDate(max);
                setStartDate(min);
                setEndDate(max);

                // Extract authors
                const uniqueAuthors = [...new Set(data.map(e => e.author_name).filter(Boolean))].sort();
                setAvailableAuthors(uniqueAuthors);
                const initialSelectedConf = {};
                uniqueAuthors.forEach(a => initialSelectedConf[a] = true);
                setSelectedAuthors(initialSelectedConf);

                // Initial Processing Setup
                let processedEntries = data;
                processedEntries = processedEntries.map(entry => {
                    let images = [];
                    if (entry.attachments) {
                        images = entry.attachments.filter(att => att.file_type === 'image');
                    }
                    return {
                        ...entry,
                        isIncluded: true,
                        selectedAttachments: images
                    };
                });
                setAllEntries(processedEntries);
                setShowPreview(true);
            } else {
                setAllEntries([]);
                setShowPreview(true);
            }
        } catch (err) {
            console.error("Fehler beim Laden der Projekt-Daten:", err);
        }
    };

    const displayEntries = useMemo(() => {
        let result = [...allEntries];

        if (useTimeframe) {
            if (startDate) result = result.filter(e => e.date >= startDate);
            if (endDate) result = result.filter(e => e.date <= endDate);
        }

        const activePrios = Object.keys(selectedPriorities).filter(k => selectedPriorities[k]);
        if (activePrios.length > 0 && activePrios.length < 4) {
            result = result.filter(e => activePrios.includes(e.problem_priority));
        } else if (activePrios.length === 0) {
            return [];
        }

        if (availableAuthors.length > 1) {
            result = result.filter(entry => selectedAuthors[entry.author_name] !== false);
        }

        result.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();

            if (sortOrder === 'date_asc') return dateA - dateB;
            if (sortOrder === 'date_desc') return dateB - dateA;

            const prioValues = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
            const prioA = prioValues[a.problem_priority] || 0;
            const prioB = prioValues[b.problem_priority] || 0;

            if (sortOrder === 'priority_desc') {
                if (prioB !== prioA) return prioB - prioA;
                return dateB - dateA;
            }
            if (sortOrder === 'priority_asc') {
                if (prioA !== prioB) return prioA - prioB;
                return dateA - dateB;
            }
            return 0;
        });

        return result;
    }, [allEntries, useTimeframe, startDate, endDate, selectedPriorities, availableAuthors, selectedAuthors, sortOrder]);

    const handleResetFilters = () => {
        setUseTimeframe(false);
        setUsePriorities(false);
        setStartDate(projectMinDate);
        setEndDate(projectMaxDate);
        setSelectedPriorities({ critical: true, high: true, medium: true, low: true });
        setSortOrder('date_desc');
        const resetAuthors = {};
        availableAuthors.forEach(a => resetAuthors[a] = true);
        setSelectedAuthors(resetAuthors);
        setShowPreview(false);
        // Da wir reines Frontend-Filtering nehmen, wird showPreview direkt auf true gesetzt wenn allEntries geladen sind.
        setTimeout(() => setShowPreview(true), 10);
    };

    const handleToggleEntry = (entryId) => {
        setAllEntries(prev => prev.map(entry => {
            if (entry.id !== entryId) return entry;
            return { ...entry, isIncluded: !entry.isIncluded };
        }));
    };

    const handleToggleAttachment = (entryId, attachmentId) => {
        setAllEntries(prev => prev.map(entry => {
            if (entry.id !== entryId) return entry;

            const isSelected = entry.selectedAttachments.some(a => a.id === attachmentId);
            let newSelected;

            if (isSelected) {
                newSelected = entry.selectedAttachments.filter(a => a.id !== attachmentId);
            } else {
                const attToAdd = entry.attachments.find(a => a.id === attachmentId);
                newSelected = [...entry.selectedAttachments, attToAdd];
            }

            return { ...entry, selectedAttachments: newSelected };
        }));
    };

    const togglePriority = (prio) => {
        setSelectedPriorities(prev => ({
            ...prev,
            [prio]: !prev[prio]
        }));
    };

    const handleSelectAllPrios = (selectAll) => {
        setSelectedPriorities({
            critical: selectAll,
            high: selectAll,
            medium: selectAll,
            low: selectAll
        });
    };

    const toggleAuthor = (authorName) => {
        setSelectedAuthors(prev => ({
            ...prev,
            [authorName]: !prev[authorName]
        }));
    };

    const handleSelectAllAuthors = (selectAll) => {
        const newSel = {};
        availableAuthors.forEach(a => newSel[a] = selectAll);
        setSelectedAuthors(newSel);
    };

    const getFilterText = () => {
        let texts = [];

        let hasFilter = false;
        if (useTimeframe) {
            texts.push(`Zeitraum: ${startDate ? new Date(startDate).toLocaleDateString('de-DE') : 'Start'} bis ${endDate ? new Date(endDate).toLocaleDateString('de-DE') : 'Heute'}`);
            hasFilter = true;
        }
        const activePrios = Object.keys(selectedPriorities).filter(k => selectedPriorities[k]);
        if (activePrios.length > 0 && activePrios.length < 4) {
            texts.push(`Priorität: ${activePrios.join(', ')}`);
            hasFilter = true;
        } else if (activePrios.length === 0) {
            texts.push('Priorität: Keine');
            hasFilter = true;
        }

        if (availableAuthors.length > 1) {
            const activeAuthors = Object.keys(selectedAuthors).filter(k => selectedAuthors[k]);
            if (activeAuthors.length > 0 && activeAuthors.length < availableAuthors.length) {
                texts.push(`Personen: ${activeAuthors.join(', ')}`);
                hasFilter = true;
            } else if (activeAuthors.length === 0) {
                texts.push('Personen: Keine ausgewählt');
                hasFilter = true;
            }
        }

        if (!hasFilter) {
            texts.push('Gesamtes Projekt');
        }

        // Count manuals exclusions
        const excludedCount = displayEntries.filter(e => !e.isIncluded).length;
        if (excludedCount > 0) {
            texts.push(`(${excludedCount} Einträge ausgeschlossen)`);
        }

        return texts.join(' | ');
    };
    if (!isOpen) return null;

    const hasActiveFilters = useTimeframe ||
        Object.values(selectedPriorities).some(v => !v) ||
        Object.values(selectedAuthors).some(v => !v) ||
        sortOrder !== 'date_desc';

    // Das End-Dokument rendert nur die inkludierten Einträge
    const finalEntries = displayEntries.filter(e => e.isIncluded);
    const datePrefix = new Date().toISOString().split('T')[0];
    const safeReportTitle = reportTitle.replace(/[^a-z0-9äöüß\s-]/gi, '_').trim().toLowerCase().replace(/\s+/g, '_');
    const pdfFilename = `${datePrefix}_${safeReportTitle}.pdf`;

    // Helpers für UI Kacheln
    const priorityBg = (prio) => {
        if (prio === 'critical') return '#fee2e2';
        if (prio === 'high') return '#ffedd5';
        if (prio === 'medium') return '#fef3c7';
        return '#f1f5f9';
    };
    const priorityColor = (prio) => {
        if (prio === 'critical') return '#991b1b';
        if (prio === 'high') return '#c2410c';
        if (prio === 'medium') return '#b45309';
        return '#475569';
    };
    const priorityBorder = (prio) => {
        if (prio === 'critical') return '#fca5a5';
        if (prio === 'high') return '#fdba74';
        if (prio === 'medium') return '#fde68a';
        return '#cbd5e1';
    };
    const priorityLabel = (prio) => {
        if (prio === 'critical') return 'Kritisch';
        if (prio === 'high') return 'Hoch';
        if (prio === 'medium') return 'Mittel';
        return 'Niedrig';
    };

    return (
        <div className="modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp} style={{ zIndex: 9999 }}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '95vw', height: '95vh', maxWidth: '1600px', display: 'flex', flexDirection: 'column', padding: '15px 25px' }}>
                <div className="modal-header" style={{ marginBottom: '15px' }}>
                    <h2>PDF Export: {project?.name}</h2>
                    <button onClick={onClose} className="btn-close-text">&times;</button>
                </div>

                <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: '20px' }}>
                    {/* Left Sidebar - Configuration */}
                    <div style={{ width: '300px', minWidth: '300px', backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', overflowY: 'auto', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, whiteSpace: 'nowrap', fontSize: '1.1rem' }}>Filter & Sortierung</h3>
                            {hasActiveFilters && (
                                <button onClick={handleResetFilters} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                                    Zurücksetzen
                                </button>
                            )}
                        </div>

                        <div className="form-group" style={{ marginBottom: '5px' }}>
                            <label>Dokumentenname / Titel</label>
                            <input
                                type="text"
                                value={reportTitle}
                                onChange={e => setReportTitle(e.target.value)}
                                placeholder="z.B. Bautagebuch Woche 12"
                            />
                        </div>

                        <div style={{ marginTop: '10px' }}>
                            <button
                                onClick={() => setIsFreeTextModalOpen(true)}
                                style={{ width: '100%', padding: '8px 12px', background: '#fff', border: '1px solid #3b82f6', color: '#3b82f6', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold' }}
                            >
                                <span>📝</span> Freitext hinzufügen {freeTextHtml && '✓'}
                            </button>
                        </div>

                        {freeTextHtml && (
                            <div className="form-group" style={{ marginTop: '15px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Position Freitext</label>
                                <select
                                    value={freeTextPosition}
                                    onChange={e => setFreeTextPosition(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                                >
                                    <option value="between_cover_team">Nach dem Deckblatt</option>
                                    <option value="between_team_entries">Vor den Einträgen</option>
                                    <option value="after_entries">Am Ende</option>
                                </select>
                            </div>
                        )}

                        <hr style={{ borderColor: '#cbd5e1', margin: '15px 0' }} />

                        {/* Deckblatt Optionen (Collapsible) */}
                        <div style={{ marginBottom: '15px' }}>
                            <button
                                onClick={() => setIsCoverOpen(!isCoverOpen)}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '10px 12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', color: '#1e293b' }}
                            >
                                Deckblatt-Optionen
                                <span>{isCoverOpen ? '▼' : '▶'}</span>
                            </button>

                            {isCoverOpen && (
                                <div style={{ padding: '12px', background: '#fff', border: '1px solid #cbd5e1', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '-4px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                            <input type="checkbox" checked={coverOptions.showLocation} onChange={e => setCoverOptions(prev => ({ ...prev, showLocation: e.target.checked }))} />
                                            Standort anzeigen
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                            <input type="checkbox" checked={coverOptions.showStatus} onChange={e => setCoverOptions(prev => ({ ...prev, showStatus: e.target.checked }))} />
                                            Projektstatus anzeigen
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={coverOptions.showTeam}
                                                onChange={e => {
                                                    const checked = e.target.checked;
                                                    setCoverOptions(prev => ({
                                                        ...prev,
                                                        showTeam: checked,
                                                        showBlacklist: checked ? true : prev.showBlacklist
                                                    }));
                                                }}
                                            />
                                            Teamliste exportieren
                                        </label>
                                        {coverOptions.showTeam && (
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', marginLeft: '20px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={coverOptions.showBlacklist}
                                                    onChange={e => setCoverOptions(prev => ({ ...prev, showBlacklist: e.target.checked }))}
                                                />
                                                Blacklist mit exportieren
                                            </label>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Zeitraum Filter (Collapsible) */}
                        <div style={{ marginBottom: '15px' }}>
                            <button
                                onClick={() => setIsTimeframeOpen(!isTimeframeOpen)}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '10px 12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', color: '#1e293b' }}
                            >
                                Zeitraum
                                <span>{isTimeframeOpen ? '▼' : '▶'}</span>
                            </button>

                            {isTimeframeOpen && (
                                <div style={{ padding: '12px', background: '#fff', border: '1px solid #cbd5e1', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', marginTop: '-4px' }}>
                                    <select
                                        value={useTimeframe ? 'custom' : 'all'}
                                        onChange={e => setUseTimeframe(e.target.value === 'custom')}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', marginBottom: '10px' }}
                                    >
                                        <option value="all">Gesamter Zeitraum</option>
                                        <option value="custom">Benutzerdefinierter Zeitraum</option>
                                    </select>

                                    {useTimeframe && (
                                        <>
                                            <div className="form-group" style={{ marginBottom: '10px' }}>
                                                <label style={{ fontSize: '0.85rem' }}>Von</label>
                                                <input
                                                    type="date"
                                                    value={startDate}
                                                    min={projectMinDate}
                                                    max={projectMaxDate}
                                                    onChange={e => setStartDate(e.target.value)}
                                                    style={{ padding: '6px' }}
                                                />
                                            </div>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label style={{ fontSize: '0.85rem' }}>Bis</label>
                                                <input
                                                    type="date"
                                                    value={endDate}
                                                    min={projectMinDate}
                                                    max={projectMaxDate}
                                                    onChange={e => setEndDate(e.target.value)}
                                                    style={{ padding: '6px' }}
                                                />
                                            </div>
                                            <small style={{ color: '#64748b', display: 'block', marginTop: '8px', fontSize: '0.75rem' }}>
                                                Verfügbar: {projectMinDate ? new Date(projectMinDate).toLocaleDateString('de-DE') : '-'} bis {projectMaxDate ? new Date(projectMaxDate).toLocaleDateString('de-DE') : '-'}
                                            </small>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Prioritäten Filter (Collapsible) */}
                        <div style={{ marginBottom: '15px' }}>
                            <button
                                onClick={() => setIsPrioOpen(!isPrioOpen)}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '10px 12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', color: '#1e293b' }}
                            >
                                Prioritäten
                                <span>{isPrioOpen ? '▼' : '▶'}</span>
                            </button>

                            {isPrioOpen && (
                                <div style={{ padding: '12px', background: '#fff', border: '1px solid #cbd5e1', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', marginTop: '-4px' }}>
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                                        <button onClick={() => handleSelectAllPrios(true)} style={{ flex: 1, padding: '6px', fontSize: '0.85rem', background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Alle</button>
                                        <button onClick={() => handleSelectAllPrios(false)} style={{ flex: 1, padding: '6px', fontSize: '0.85rem', background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Keine</button>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={selectedPriorities.critical} onChange={() => togglePriority('critical')} />
                                            <span style={{ color: '#991b1b' }}>Kritisch</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={selectedPriorities.high} onChange={() => togglePriority('high')} />
                                            <span style={{ color: '#c2410c' }}>Hoch</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={selectedPriorities.medium} onChange={() => togglePriority('medium')} />
                                            <span style={{ color: '#b45309' }}>Mittel</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={selectedPriorities.low} onChange={() => togglePriority('low')} />
                                            <span style={{ color: '#15803d' }}>Niedrig</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Person Filter (Collapsible) */}
                        {availableAuthors.length > 1 && (
                            <div style={{ marginBottom: '15px' }}>
                                <button
                                    onClick={() => setIsAuthorOpen(!isAuthorOpen)}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '10px 12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', color: '#1e293b' }}
                                >
                                    Personen
                                    <span>{isAuthorOpen ? '▼' : '▶'}</span>
                                </button>

                                {isAuthorOpen && (
                                    <div style={{ padding: '12px', background: '#fff', border: '1px solid #cbd5e1', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', marginTop: '-4px' }}>
                                        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                                            <button onClick={() => handleSelectAllAuthors(true)} style={{ flex: 1, padding: '6px', fontSize: '0.85rem', background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Alle</button>
                                            <button onClick={() => handleSelectAllAuthors(false)} style={{ flex: 1, padding: '6px', fontSize: '0.85rem', background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Keine</button>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                                            {availableAuthors.map(author => (
                                                <label key={author} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={selectedAuthors[author] !== false} onChange={() => toggleAuthor(author)} />
                                                    <span style={{ color: '#1e293b' }}>{author}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label>Sortierung</label>
                            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                                <option value="date_desc">Datum (Neu -{'>'} Alt)</option>
                                <option value="date_asc">Datum (Alt -{'>'} Neu)</option>
                                <option value="priority_desc">Priorität (Kritisch -{'>'} Niedrig)</option>
                                <option value="priority_asc">Priorität (Niedrig -{'>'} Kritisch)</option>
                            </select>
                        </div>


                        {showPreview && (
                            <div style={{ marginTop: 'auto', borderTop: '1px solid #cbd5e1', paddingTop: '20px' }}>
                                <h3 style={{ margin: '0 0 15px 0' }}>Exportieren</h3>
                                <PDFDownloadLink
                                    key={`${pdfFilename}-${finalEntries.length}-${sortOrder}-${freeTextHtml}-${freeTextPosition}`}
                                    document={<PdfDocument project={project} entries={finalEntries} filterText={getFilterText()} reportTitle={reportTitle} coverOptions={coverOptions} teamMembers={teamMembers} creatorName={user?.name || user?.username} freeTextHtml={freeTextHtml} freeTextPosition={freeTextPosition} />}
                                    fileName={pdfFilename}
                                    style={{ textDecoration: 'none' }}
                                >
                                    {({ blob, url, loading, error }) => (
                                        <button className="btn-primary" style={{ width: '100%', background: '#10b981', borderColor: '#059669', padding: '12px', fontSize: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }} disabled={loading}>
                                            <span style={{ fontSize: '1.2rem' }}>↓</span> {loading ? 'Generiere PDF...' : 'PDF Herunterladen'}
                                        </button>
                                    )}
                                </PDFDownloadLink>
                            </div>
                        )}
                    </div>

                    {/* Middle Area - Entry & Image Management */}
                    {showPreview && displayEntries.length > 0 && (
                        <>
                            {!isDetailsOpen ? (
                                <div style={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: '600px' }}>
                                    <button
                                        onClick={() => setIsDetailsOpen(true)}
                                        style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px 5px', height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.2s ease', minWidth: '40px' }}
                                        title="Details anpassen aufklappen"
                                    >
                                        <span style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>▶</span>
                                        <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 'bold', letterSpacing: '1px' }}>Details anpassen</span>
                                    </button>
                                </div>
                            ) : (
                                <div style={{ width: '350px', minWidth: '350px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', overflowY: 'auto', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ position: 'sticky', top: '-20px', backgroundColor: '#fff', zIndex: 10, padding: '20px 0 10px 0', borderBottom: '1px solid #e2e8f0', marginBottom: '15px', margin: '-20px 0 15px 0' }}>
                                        <button
                                            onClick={() => setIsDetailsOpen(false)}
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: '#1e293b' }}
                                        >
                                            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Details anpassen</h3>
                                            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>▼</span>
                                        </button>

                                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 0, marginTop: '10px' }}>
                                            Schließe komplette Einträge oder einzelne Bilder vom Export aus.
                                        </p>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {displayEntries.map(entry => {
                                            const images = entry.attachments?.filter(a => a.file_type === 'image') || [];

                                            return (
                                                <div key={`entrysel-${entry.id}`} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', opacity: entry.isIncluded ? 1 : 0.5 }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={entry.isIncluded}
                                                            onChange={() => handleToggleEntry(entry.id)}
                                                            style={{ width: '18px', height: '18px', marginTop: '4px', cursor: 'pointer' }}
                                                        />

                                                        {/* Mini Preview Card */}
                                                        <div
                                                            className="approval-card selectable-card"
                                                            style={{ flex: 1, cursor: 'pointer', margin: 0, padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                                            onClick={() => setSelectedDetailEntry(entry)}
                                                            title="Details anzeigen"
                                                        >
                                                            <div className="approval-card-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                    <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{entry.author_name}</span>
                                                                    <small style={{ color: '#64748b' }}>
                                                                        {new Date(entry.date).toLocaleDateString('de-DE')} {entry.time && `(${entry.time})`}
                                                                    </small>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    {entry.attachments?.some(a => a.file_type === 'audio') && (
                                                                        <span title="Sprachnachricht vorhanden" style={{ fontSize: '1.1rem' }}>🎙️</span>
                                                                    )}
                                                                    <div className="entry-priority-badge" style={{ fontSize: '0.7rem', padding: '2px 6px', backgroundColor: priorityBg(entry.problem_priority), color: priorityColor(entry.problem_priority), border: `1px solid ${priorityBorder(entry.problem_priority)}` }}>
                                                                        {priorityLabel(entry.problem_priority)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {entry.title && <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '4px', color: '#334155' }}>{entry.title}</div>}
                                                            <div style={{ fontSize: '0.8rem', color: '#475569', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                                {entry.content}
                                                            </div>
                                                            {entry.weather && (
                                                                <div style={{ fontSize: '0.75rem', marginTop: '6px', color: '#64748b' }}>
                                                                    ⛅ {entry.weather}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {entry.isIncluded && images.length > 0 && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginLeft: '28px', marginTop: '10px' }}>
                                                            {images.map(att => {
                                                                const isSelected = entry.selectedAttachments.some(a => a.id === att.id);
                                                                return (
                                                                    <div
                                                                        key={att.id}
                                                                        onClick={() => handleToggleAttachment(entry.id, att.id)}
                                                                        style={{
                                                                            position: 'relative',
                                                                            aspectRatio: '1',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                                                                            opacity: isSelected ? 1 : 0.3,
                                                                            transition: 'all 0.2s'
                                                                        }}
                                                                        title="Bild Ein-/Auszuschließen"
                                                                    >
                                                                        <img src={att.public_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '2px' }} />
                                                                        {isSelected && (
                                                                            <div style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#3b82f6', color: '#fff', borderRadius: '50%', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px' }}>
                                                                                ✓
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Right Area - PDF Viewer Preview */}
                    <div style={{ flex: 1, backgroundColor: '#cbd5e1', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {!showPreview ? (
                            <div style={{ margin: 'auto', color: '#64748b', textAlign: 'center', background: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                                <div style={{ fontSize: '4rem', marginBottom: '15px' }}>📄</div>
                                <h3 style={{ margin: '0 0 10px 0' }}>Keine Vorschau verfügbar</h3>
                                Konfigurieren Sie den Export links und klicken Sie auf<br /> <b>"Vorschau & Einträge laden"</b>.
                            </div>
                        ) : finalEntries.length === 0 ? (
                            <div style={{ margin: 'auto', color: '#64748b', textAlign: 'center', background: '#fff', padding: '40px', borderRadius: '12px' }}>
                                <h3 style={{ margin: '0 0 10px 0' }}>Keine Einträge für PDF ausgewählt</h3>
                                Für die gewählten Filter oder manuelle Auswahl gibt es keine Treffer.
                            </div>
                        ) : (
                            <PDFViewer key={`pdf-${sortOrder}-${finalEntries.map(e => e.id).join('-')}-${freeTextHtml}-${freeTextPosition}`} width="100%" height="100%" showToolbar={false} style={{ border: 'none' }}>
                                <PdfDocument project={project} entries={finalEntries} filterText={getFilterText()} reportTitle={reportTitle} coverOptions={coverOptions} teamMembers={teamMembers} creatorName={user?.name || user?.username} freeTextHtml={freeTextHtml} freeTextPosition={freeTextPosition} />
                            </PDFViewer>
                        )}
                    </div>
                </div>
            </div>

            {/* Entry Detail Modal (Inline) */}
            {selectedDetailEntry && (
                <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); setSelectedDetailEntry(null); }} style={{ zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-content" style={{ width: '800px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #eee', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem' }}>
                                📋 Eintrag vom {new Date(selectedDetailEntry.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </h2>
                            <button onClick={(e) => { e.stopPropagation(); setSelectedDetailEntry(null); }} className="btn-close-text" style={{ fontSize: '1.5rem' }}>&times;</button>
                        </div>

                        <div className="entry-detail-body" style={{ padding: 0 }}>
                            <div className="journal-entry-row" style={{ backgroundColor: '#fff', margin: 0, border: 'none', boxShadow: 'none' }}>
                                <div className="entry-row-sidebar" style={{ backgroundColor: priorityColor(selectedDetailEntry.problem_priority) }}></div>
                                <div className="entry-row-content" style={{ padding: '0 15px' }}>

                                    <div className="entry-row-header" style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                        <span className="entry-date" style={{ fontWeight: 'bold' }}>{new Date(selectedDetailEntry.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} {selectedDetailEntry.time && <small style={{ fontWeight: 'normal' }}>({selectedDetailEntry.time})</small>}</span>
                                        {selectedDetailEntry.title && <span className="entry-title-inline" style={{ fontWeight: 'bold', fontSize: '1.1rem', flex: 1 }}>{selectedDetailEntry.title}</span>}
                                        {!selectedDetailEntry.title && <div style={{ flex: 1 }}></div>}

                                        {selectedDetailEntry.attachments?.some(a => a.file_type === 'audio') && (
                                            <AttachmentUploader
                                                mode="icons"
                                                attachments={selectedDetailEntry.attachments}
                                            />
                                        )}
                                        <div className="entry-priority-badge" style={{ backgroundColor: priorityBg(selectedDetailEntry.problem_priority), color: priorityColor(selectedDetailEntry.problem_priority), border: `1px solid ${priorityBorder(selectedDetailEntry.problem_priority)}`, marginLeft: 'auto', order: 10 }}>
                                            {priorityLabel(selectedDetailEntry.problem_priority)}
                                        </div>
                                    </div>

                                    <div className="entry-text expanded">
                                        {selectedDetailEntry.content}
                                    </div>

                                    {selectedDetailEntry.attachments?.length > 0 && (
                                        <div onClick={ev => ev.stopPropagation()} style={{ marginTop: '10px' }}>
                                            <AttachmentUploader
                                                mode="view"
                                                entryId={selectedDetailEntry.id}
                                                token={token}
                                                attachments={selectedDetailEntry.attachments}
                                                onUpdate={() => { }} // No update action in export view
                                            />
                                        </div>
                                    )}

                                    <div className="entry-footer-new" style={{ marginTop: '15px' }}>
                                        <div className="author-info">
                                            <strong>{selectedDetailEntry.author_name}</strong>
                                            <span className="author-timestamp">
                                                {selectedDetailEntry.updated_at && selectedDetailEntry.updated_at !== selectedDetailEntry.created_at ? (
                                                    <>Geändert am {new Date(selectedDetailEntry.updated_at).toLocaleString('de-DE')}</>
                                                ) : (
                                                    <>Erfasst am {new Date(selectedDetailEntry.created_at).toLocaleString('de-DE')}</>
                                                )}
                                            </span>
                                        </div>
                                        <div className="entry-actions-inline">
                                            {formatWeatherDisplay(selectedDetailEntry) && (
                                                <span className="weather-badge">
                                                    ⛅ {formatWeatherDisplay(selectedDetailEntry)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Free Text Modal */}
            <ExportFreeTextModal
                isOpen={isFreeTextModalOpen}
                onClose={() => setIsFreeTextModalOpen(false)}
                initialHtml={freeTextHtml}
                onSave={setFreeTextHtml}
            />
        </div>
    );
}
